import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ProfileSectionPage from '../../../components/ProfileSectionPage/ProfileSectionPage';
import DeleteItemModal from '../../../components/DeleteItemModal';
import Modal from '../../../components/common/Modal';
import Button from '../../../components/common/Button';
import { PlayIcon, TrashCanIcon } from '../../../assets/svgs/icons';
import imageDisplay from '../../../assets/images/ImageDisplay.png';
import { useBrandContext } from '../../../contexts/BrandContext';
import { getBrandMedia, saveBrandMedia, setBrandMediaTag } from '../../../api/brandMedia';
import { BrandMediaInterface, BrandMediaItem, BrandMediaTag } from '../../../types/BrandMediaInterface';
import { validateMediaUpload } from '../../../utils/media';
import BrandProfileActions from './BrandProfileActions';
import './styles/_brand-gallery.scss';

const MAX_GALLERY_ITEMS = 10;
const GALLERY_ACCEPT = 'image/.jpg,.jpeg,.png,.gif,.webp,video/.mp4,.MP4,.mov,.MOV,.webm';
const TAGS: { tag: BrandMediaTag; label: string }[] = [
  { tag: 'food', label: 'FOOD' },
  { tag: 'drink', label: 'DRINK' },
  { tag: 'vibe', label: 'VIBE' }
];

const galleryIDs = (items: BrandMediaItem[]) => items.map((item) => String(item.id));

const BrandGalleryPage = () => {
  const { currentBrand } = useBrandContext();
  const [gallery, setGallery] = useState<BrandMediaItem[] | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [itemToTag, setItemToTag] = useState<BrandMediaItem | null>(null);
  const [itemToDelete, setItemToDelete] = useState<BrandMediaItem | null>(null);

  useEffect(() => {
    if (!currentBrand) return;
    getBrandMedia(currentBrand.id)
      .then((media) => setGallery(media.gallery))
      .catch((error) => {
        console.error('Failed to load brand gallery:', error);
        toast.error('Failed to load gallery.');
      });
  }, [currentBrand]);

  const save = async (change: () => Promise<BrandMediaInterface>, failure: string) => {
    setIsBusy(true);
    try {
      const media = await change();
      setGallery(media.gallery);
      return media;
    } catch (error) {
      console.error(failure, error);
      toast.error(failure);
      return null;
    } finally {
      setIsBusy(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!currentBrand || !gallery || !file) return;

    try {
      await validateMediaUpload(event.target.files);
    } catch (error) {
      toast.error((error as Error).message);
      return;
    }

    const media = await save(
      () => saveBrandMedia(currentBrand.id, { gallery: [file], galleryOrder: [...galleryIDs(gallery), 'filename-0'] }),
      'Failed to upload to the gallery.'
    );
    const uploaded = media?.gallery.find((item) => !gallery.some(({ id }) => id === item.id));
    if (uploaded) setItemToTag(uploaded);
  };

  const handleTag = async (tag: BrandMediaTag) => {
    if (!currentBrand || !itemToTag) return;
    const { id } = itemToTag;
    setItemToTag(null);
    await save(() => setBrandMediaTag(currentBrand.id, id, tag), 'Failed to tag the gallery item.');
  };

  const handleDelete = async () => {
    if (!currentBrand || !gallery || !itemToDelete) return;
    const { id } = itemToDelete;
    setItemToDelete(null);
    await save(
      () =>
        saveBrandMedia(currentBrand.id, {
          mediaToDelete: [id],
          galleryOrder: galleryIDs(gallery.filter((item) => item.id !== id))
        }),
      'Failed to delete the gallery item.'
    );
  };

  return (
    <ProfileSectionPage title="GALLERY">
      <div className="brand-gallery-page">
        <p className="brand-gallery-description">
          Choose your favorite restaurant photos for your homepage. These photos will be displayed on your homepage.
          The photos should be at least 100 px wide &amp; 100 px tall and will be cropped to fit a 4:6 aspect ratio.
        </p>

        <div className="brand-gallery-grid">
          {gallery && gallery.length < MAX_GALLERY_ITEMS && (
            <label htmlFor="brandGalleryUpload" className={`brand-gallery-upload ${isBusy ? 'is-busy' : ''}`}>
              <img src={imageDisplay} alt="" />
              <span>Select Photo</span>
              <input
                id="brandGalleryUpload"
                type="file"
                accept={GALLERY_ACCEPT}
                disabled={isBusy}
                onChange={handleUpload}
                onClick={(event) => {
                  // eslint-disable-next-line no-param-reassign
                  (event.target as HTMLInputElement).value = '';
                }}
              />
            </label>
          )}

          {gallery?.map((item) => (
            <div key={item.id} className="brand-gallery-item">
              {item.mediaType === 'video' ? (
                <>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <video src={item.url} preload="metadata" />
                  <PlayIcon />
                </>
              ) : (
                <img src={item.url} alt={item.tag ? `${item.tag} photo` : 'Gallery photo'} />
              )}
              <TrashCanIcon color="white" width="12" onIconClicked={() => setItemToDelete(item)} />
            </div>
          ))}
        </div>

        <BrandProfileActions />
      </div>

      {itemToTag && (
        <Modal className="brand-gallery-tag-modal">
          <div className="modal-header">
            <h1 className="modal-title">TAG THIS ITEM</h1>
          </div>
          <div className="modal-body">
            <div className="modal-message">What does this show?</div>
            <div className="brand-gallery-tag-options">
              {TAGS.map(({ tag, label }) => (
                <Button key={tag} onClick={() => handleTag(tag)} className="brand-gallery-tag-button">
                  {label}
                </Button>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <Button onClick={() => setItemToTag(null)} className="modal-cancel-button">
              SKIP
            </Button>
          </div>
        </Modal>
      )}

      <DeleteItemModal
        title="DELETE ITEM"
        message="Please confirm that you would like to delete this item."
        isOpen={!!itemToDelete}
        onCancel={() => setItemToDelete(null)}
        onConfirm={handleDelete}
      />
    </ProfileSectionPage>
  );
};

export default BrandGalleryPage;
